/* eslint-disable */
import { useState, useEffect, useCallback } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Plus, Edit3, Trash2, X, Package, Hash, Image as ImageIcon, Tag, Settings, ChevronDown } from "lucide-react";
import ImageField from "@/components/ImageField";

const EMPTY_PRODUCT = {
  title: "", slug: "", description: "", main_image: "",
  category_id: "", price: "", quantity: "", status: "active", tags: [],
  on_sale: false, sale_price: "",
  available_online: false,
  location: "The Village Antique and Vendor Market, Kingsport, TN — Booth I-12",
};

export default function AdminMerchMore() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showCatForm, setShowCatForm] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", code: "", description: "" });
  const [editCat, setEditCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [printTagProduct, setPrintTagProduct] = useState(null);
  const [showTagManager, setShowTagManager] = useState(false);
  const [tagForm, setTagForm] = useState("");
  const [editTag, setEditTag] = useState(null);
  const [editTagName, setEditTagName] = useState("");
  const [pageSettings, setPageSettings] = useState(null);
  const [showPageSettings, setShowPageSettings] = useState(false);
  const [savingPage, setSavingPage] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cRes, pRes, tRes, psRes] = await Promise.all([
        http.get("/marker-collection/admin/merch/categories"),
        http.get("/marker-collection/admin/merch/products"),
        http.get("/marker-collection/admin/merch/tags"),
        http.get("/marker-collection/admin/market/page-settings").catch(() => ({ data: null })),
      ]);
      setCategories(cRes.data || []);
      setProducts(pRes.data || []);
      setTags(tRes.data || []);
      if (psRes.data) setPageSettings(psRes.data);
    } catch { toast.error("Failed to load merch data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Category CRUD ───
  const saveCategory = async () => {
    if (!catForm.name.trim() || !catForm.code.trim()) {
      toast.error("Name and code are required"); return;
    }
    setSaving(true);
    try {
      if (editCat) {
        await http.patch(`/marker-collection/admin/merch/categories/${editCat.id}`, {
          name: catForm.name, description: catForm.description,
        });
        toast.success("Category updated");
      } else {
        await http.post("/marker-collection/admin/merch/categories", catForm);
        toast.success("Category created");
      }
      setShowCatForm(false); setEditCat(null);
      setCatForm({ name: "", code: "", description: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save category"); }
    finally { setSaving(false); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await http.delete(`/marker-collection/admin/merch/categories/${id}`);
      toast.success("Category deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  // ─── Product CRUD ───
  const saveProduct = async () => {
    if (!editProduct) return;
    const { title, category_id, price } = editProduct;
    if (!title?.trim()) { toast.error("Title is required"); return; }
    if (!category_id) { toast.error("Select a category"); return; }
    setSaving(true);
    try {
      const payload = { ...editProduct, price: parseFloat(price) || 0,
        sale_price: editProduct.sale_price != null && editProduct.sale_price !== "" ? parseFloat(editProduct.sale_price) : null,
        on_sale: !!editProduct.on_sale,
        available_online: !!editProduct.available_online,
        location: editProduct.location || "The Village Antique and Vendor Market, Kingsport, TN — Booth I-12",
      };
      if (payload.quantity === "" || payload.quantity === null) payload.quantity = null;
      else payload.quantity = parseInt(payload.quantity);
      if (editProduct.id) {
        await http.patch(`/marker-collection/admin/merch/products/${editProduct.id}`, payload);
        toast.success("Product updated");
      } else {
        await http.post("/marker-collection/admin/merch/products", payload);
        toast.success("Product created");
      }
      setEditProduct(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save product"); }
    finally { setSaving(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await http.delete(`/marker-collection/admin/merch/products/${id}`);
      toast.success("Product deleted"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete"); }
  };

  const set = (key, val) => setEditProduct(p => ({ ...p, [key]: val }));

  // ─── Tag CRUD ───
  const createTag = async () => {
    const name = tagForm.trim();
    if (!name) return;
    try {
      await http.post("/marker-collection/admin/merch/tags", { name });
      toast.success(`Tag "${name}" created`);
      setTagForm(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateTag = async () => {
    if (!editTag || !editTagName.trim()) return;
    try {
      await http.patch(`/marker-collection/admin/merch/tags/${editTag.id}`, { name: editTagName.trim() });
      toast.success("Tag updated"); setEditTag(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const deleteTag = async (id) => {
    if (!window.confirm("Delete this tag? It will be removed from all products.")) return;
    try {
      await http.delete(`/marker-collection/admin/merch/tags/${id}`);
      toast.success("Tag deleted"); load();
    } catch { toast.error("Failed"); }
  };

  const toggleProductTag = (tagName) => {
    const current = editProduct?.tags || [];
    if (current.includes(tagName)) {
      set("tags", current.filter(t => t !== tagName));
    } else {
      set("tags", [...current, tagName]);
    }
  };

  const savePageSettings = async () => {
    if (!pageSettings) return;
    setSavingPage(true);
    try {
      await http.put("/marker-collection/admin/market/page-settings", pageSettings);
      toast.success("Page settings saved");
    } catch { toast.error("Failed to save page settings"); }
    finally { setSavingPage(false); }
  };

  const setPS = (k, v) => setPageSettings(prev => ({ ...prev, [k]: v }));

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="animate-spin text-[#C9A055]" size={24} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="merch-admin-panel">

      {/* ─── Page Settings ─── */}
      <div className="border border-white/10 bg-[#0d0c0a]" data-testid="market-page-settings">
        <button onClick={() => setShowPageSettings(!showPageSettings)}
          className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors"
          data-testid="market-page-settings-toggle">
          <div className="flex items-center gap-2">
            <Settings size={14} className="text-[#C9A055]" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">
              MARKET PAGE SETTINGS
            </span>
          </div>
          <ChevronDown size={14} className={`text-[#6a6660] transition-transform ${showPageSettings ? "rotate-180" : ""}`} />
        </button>

        {showPageSettings && pageSettings && (
          <div className="px-5 pb-5 pt-0 space-y-4 border-t border-white/5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">SUBTITLE</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.subtitle}
                  onChange={e => setPS("subtitle", e.target.value)} placeholder="BY THE MARKER COLLECTION"
                  data-testid="mps-subtitle" />
              </div>
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">PAGE TITLE</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.title}
                  onChange={e => setPS("title", e.target.value)} placeholder="THE MARKET"
                  data-testid="mps-title" />
              </div>
            </div>
            <div>
              <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">DESCRIPTION</label>
              <textarea className="stc-input w-full text-[12px] min-h-[60px]" value={pageSettings.description}
                onChange={e => setPS("description", e.target.value)}
                placeholder="Hats, books, prints, and more..."
                data-testid="mps-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">BADGE LEFT (e.g. Free Shipping $75+)</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.badge_left}
                  onChange={e => setPS("badge_left", e.target.value)} placeholder="Free Shipping $75+"
                  data-testid="mps-badge-left" />
              </div>
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">BADGE RIGHT (e.g. In-Person & Online)</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.badge_right}
                  onChange={e => setPS("badge_right", e.target.value)} placeholder="In-Person & Online"
                  data-testid="mps-badge-right" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">FOOTER CTA TEXT</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.cta_text}
                  onChange={e => setPS("cta_text", e.target.value)} placeholder="LOOKING FOR ORIGINAL ART?"
                  data-testid="mps-cta-text" />
              </div>
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">CTA BUTTON TEXT</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.cta_button}
                  onChange={e => setPS("cta_button", e.target.value)} placeholder="Visit The Marker Collection"
                  data-testid="mps-cta-button" />
              </div>
              <div>
                <label className="font-ui text-[8px] tracking-[0.25em] text-[#8a857d] block mb-1">CTA LINK</label>
                <input className="stc-input w-full text-[12px]" value={pageSettings.cta_link}
                  onChange={e => setPS("cta_link", e.target.value)} placeholder="/marker-collection"
                  data-testid="mps-cta-link" />
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={savePageSettings} disabled={savingPage}
                className="px-5 py-2.5 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] disabled:opacity-50 transition-colors"
                data-testid="mps-save">
                {savingPage ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Page Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Categories Section ─── */}
      <div className="border border-white/10 bg-[#0d0c0a] p-5" data-testid="merch-categories-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-[#C9A055]" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">
              PRODUCT CATEGORIES ({categories.length})
            </span>
          </div>
          <button onClick={() => { setShowCatForm(true); setEditCat(null); setCatForm({ name: "", code: "", description: "" }); }}
            className="bg-[#C9A055] text-black px-3 py-1.5 font-ui text-[9px] tracking-[0.2em] uppercase flex items-center gap-1.5 hover:bg-[#b8915a]"
            data-testid="merch-add-category-btn">
            <Plus size={12} /> Add Category
          </button>
        </div>

        {/* Category List */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {categories.map(cat => (
            <div key={cat.id} className="border border-white/10 bg-[#0a0a0a] p-3 group" data-testid={`merch-cat-${cat.code}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold font-mono tracking-wider bg-[#C9A055]/15 text-[#C9A055] px-2 py-0.5">{cat.code}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditCat(cat); setCatForm({ name: cat.name, code: cat.code, description: cat.description || "" }); setShowCatForm(true); }}
                    className="p-0.5 text-[#8a857d] hover:text-white"><Edit3 size={10} /></button>
                  <button onClick={() => deleteCategory(cat.id)}
                    className="p-0.5 text-[#8a857d] hover:text-red-400"><Trash2 size={10} /></button>
                </div>
              </div>
              <p className="text-[11px] text-[#F5F2EB] font-medium">{cat.name}</p>
              {cat.description && <p className="text-[9px] text-[#6a6660] mt-0.5">{cat.description}</p>}
              <p className="text-[9px] text-[#6a6660] mt-1 font-mono">MT26-{cat.code}XXXX</p>
            </div>
          ))}
        </div>

        {/* Category Form Modal */}
        {showCatForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowCatForm(false)}>
            <div className="bg-[#141210] border border-white/10 max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="merch-category-form">
              <h3 className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055] mb-4">
                {editCat ? "Edit Category" : "New Category"}
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">NAME</label>
                  <input className="stc-input w-full text-[12px]" value={catForm.name}
                    onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., Stickers" data-testid="merch-cat-name-input" />
                </div>
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">CODE (2-5 letters, used in vendor codes)</label>
                  <input className="stc-input w-full text-[12px] font-mono uppercase" value={catForm.code}
                    onChange={e => setCatForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5) }))}
                    placeholder="e.g., STK" maxLength={5} disabled={!!editCat}
                    data-testid="merch-cat-code-input" />
                  {!editCat && <p className="text-[8px] text-[#6a6660] mt-1">Vendor codes will be: MT26-{catForm.code || "XXX"}0001</p>}
                </div>
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">DESCRIPTION (optional)</label>
                  <input className="stc-input w-full text-[12px]" value={catForm.description}
                    onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Short description" data-testid="merch-cat-desc-input" />
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={saveCategory} disabled={saving}
                  className="bg-[#C9A055] text-black px-5 py-2 font-ui text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 hover:bg-[#b8915a] disabled:opacity-50"
                  data-testid="merch-cat-save-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {editCat ? "Update" : "Create"}
                </button>
                <button onClick={() => setShowCatForm(false)}
                  className="btn-outline text-[10px] px-4 py-2">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Tags Section ─── */}
      <div className="border border-white/10 bg-[#0d0c0a] p-5" data-testid="merch-tags-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Tag size={14} className="text-purple-400" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-purple-400">
              PRODUCT TAGS ({tags.length})
            </span>
          </div>
          <button onClick={() => setShowTagManager(!showTagManager)}
            className="font-ui text-[9px] tracking-[0.15em] uppercase text-[#8a857d] hover:text-white transition-colors"
            data-testid="merch-toggle-tag-manager">
            {showTagManager ? "COLLAPSE" : "MANAGE TAGS"}
          </button>
        </div>

        {/* Tag Pills (always visible) */}
        <div className="flex flex-wrap gap-1.5">
          {tags.map(t => (
            <span key={t.id} className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-wider uppercase bg-purple-900/20 text-purple-300 border border-purple-500/20"
              data-testid={`merch-tag-pill-${t.slug}`}>
              {t.name}
              {showTagManager && (
                <span className="inline-flex gap-0.5">
                  <button onClick={() => { setEditTag(t); setEditTagName(t.name); }}
                    className="ml-0.5 text-purple-400 hover:text-purple-200"><Edit3 size={9} /></button>
                  <button onClick={() => deleteTag(t.id)}
                    className="text-red-400 hover:text-red-300"><X size={9} /></button>
                </span>
              )}
            </span>
          ))}
        </div>

        {/* Add/Edit Tag */}
        {showTagManager && (
          <div className="mt-3 flex gap-2 items-center">
            {editTag ? (
              <span className="contents">
                <input className="stc-input text-[12px] flex-1" value={editTagName}
                  onChange={e => setEditTagName(e.target.value)} placeholder="Rename tag..."
                  onKeyDown={e => e.key === "Enter" && updateTag()}
                  data-testid="merch-edit-tag-input" />
                <button onClick={updateTag}
                  className="bg-purple-600 text-white px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase hover:bg-purple-500"
                  data-testid="merch-update-tag-btn">Save</button>
                <button onClick={() => setEditTag(null)}
                  className="text-[#8a857d] hover:text-white text-[10px]">Cancel</button>
              </span>
            ) : (
              <span className="contents">
                <input className="stc-input text-[12px] flex-1" value={tagForm}
                  onChange={e => setTagForm(e.target.value)} placeholder="New tag name..."
                  onKeyDown={e => e.key === "Enter" && createTag()}
                  data-testid="merch-new-tag-input" />
                <button onClick={createTag}
                  className="bg-purple-600 text-white px-3 py-1.5 font-ui text-[9px] tracking-[0.15em] uppercase flex items-center gap-1 hover:bg-purple-500"
                  data-testid="merch-add-tag-btn">
                  <Plus size={10} /> Add Tag
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ─── Products Section ─── */}
      <div data-testid="merch-products-section">
        <div className="flex items-center justify-between mb-4">
          <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">
            {products.length} Product{products.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-2">
            <button onClick={() => {
              const tagsHtml = products.filter(p => p.status === "active").map(p => generateMerchTagHtml(p)).join('<div style="page-break-after:always;height:0;"></div>');
              if (!tagsHtml) { toast.error("No active products"); return; }
              const win = window.open("", "_blank", "width=700,height=500");
              win.document.write(`<!DOCTYPE html><html><head><title>Merch Price Tags</title><link rel="preconnect" href="https://fonts.googleapis.com"><link href="https://fonts.googleapis.com/css2?family=Saira+Condensed:wght@700;800;900&family=DM+Sans:wght@400;500;700&family=Permanent+Marker&display=swap" rel="stylesheet"><style>@page{size:6in 4in landscape;margin:0}*{margin:0;padding:0;box-sizing:border-box}body{background:#fff}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>${tagsHtml}</body></html>`);
              win.document.close();
              win.onload = () => { win.focus(); win.print(); };
            }}
              className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5 border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055]/10"
              data-testid="merch-batch-print-tags">
              <Package size={12} /> Print All Tags
            </button>
            <button onClick={() => setEditProduct({ ...EMPTY_PRODUCT })}
              className="bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-1.5 hover:bg-[#b8915a]"
              data-testid="merch-add-product-btn">
              <Plus size={14} /> Add Product
            </button>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="border-2 border-dashed border-white/10 text-center py-12">
            <Package size={32} className="mx-auto mb-3 text-gray-600" />
            <p className="text-[#8a857d] text-sm">No products yet. Add your first merch item!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {products.map(prod => (
              <ProductRow key={prod.id} prod={prod} categories={categories}
                onEdit={() => setEditProduct({ ...prod, price: prod.price ?? "", quantity: prod.quantity ?? "" })}
                onDelete={() => deleteProduct(prod.id)}
                onPrintTag={() => setPrintTagProduct(prod)} />
            ))}
          </div>
        )}
      </div>

      {/* ─── Product Editor Modal ─── */}
      {editProduct && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto" onClick={() => setEditProduct(null)}>
          <div className="bg-[#141210] border border-white/10 max-w-2xl w-full my-2 sm:my-8 max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="merch-product-editor">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">
                {editProduct.id ? "Edit Product" : "New Product"}
              </span>
              <button onClick={() => setEditProduct(null)} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">TITLE</label>
                <input className="stc-input w-full text-[12px]" value={editProduct.title}
                  onChange={e => set("title", e.target.value)} placeholder="Product name"
                  data-testid="merch-product-title" />
              </div>
              {/* Category */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">CATEGORY</label>
                <select className="stc-input w-full text-[12px]" value={editProduct.category_id}
                  onChange={e => set("category_id", e.target.value)}
                  data-testid="merch-product-category">
                  <option value="">Select category...</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                  ))}
                </select>
              </div>
              {/* Price + Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">PRICE ($)</label>
                  <input type="number" step="0.01" className="stc-input w-full text-[12px] font-mono" value={editProduct.price}
                    onChange={e => set("price", e.target.value)} placeholder="0.00"
                    data-testid="merch-product-price" />
                  <label className="flex items-center gap-2 cursor-pointer mt-2">
                    <input type="checkbox" checked={!!editProduct.on_sale} onChange={e => set("on_sale", e.target.checked)} className="accent-red-500 w-4 h-4" data-testid="merch-product-on-sale" />
                    <span className="font-ui text-[8px] text-red-400 tracking-[0.15em]">ON SALE</span>
                  </label>
                  {editProduct.on_sale && (
                    <div className="mt-1.5">
                      <label className="font-ui text-[8px] tracking-[0.2em] text-red-400 block mb-0.5">SALE PRICE ($)</label>
                      <input type="number" step="0.01" className="stc-input w-full text-[12px] font-mono border-red-500/30" value={editProduct.sale_price ?? ""}
                        onChange={e => set("sale_price", e.target.value === "" ? null : e.target.value)} placeholder="Sale price"
                        data-testid="merch-product-sale-price" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">QUANTITY (blank = unlimited)</label>
                  <input type="number" className="stc-input w-full text-[12px] font-mono" value={editProduct.quantity ?? ""}
                    onChange={e => set("quantity", e.target.value === "" ? null : e.target.value)}
                    placeholder="Unlimited" data-testid="merch-product-qty" />
                </div>
              </div>
              {/* Image */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">IMAGE</label>
                <ImageField label="Product Image" value={editProduct.main_image || ""} onChange={val => set("main_image", val)} testid="merch-product-image" />
              </div>
              {/* Description */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">DESCRIPTION</label>
                <textarea className="stc-input w-full text-[12px] min-h-[80px]" value={editProduct.description}
                  onChange={e => set("description", e.target.value)} placeholder="Short product description"
                  data-testid="merch-product-description" />
              </div>
              {/* Tags */}
              <div data-testid="merch-product-tags-picker">
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1.5">
                  TAGS ({(editProduct.tags || []).length} selected)
                </label>
                <div className="flex flex-wrap gap-1.5 p-3 border border-white/10 bg-black/20 rounded-sm max-h-[140px] overflow-y-auto">
                  {tags.map(t => {
                    const selected = (editProduct.tags || []).includes(t.name);
                    return (
                      <button key={t.id} type="button" onClick={() => toggleProductTag(t.name)}
                        className={`px-2.5 py-1 text-[10px] tracking-wider uppercase border transition-all ${
                          selected
                            ? "bg-purple-600/30 text-purple-200 border-purple-500/50"
                            : "bg-transparent text-[#8a857d] border-white/10 hover:border-purple-500/30 hover:text-purple-300"
                        }`}
                        data-testid={`merch-product-tag-${t.slug}`}>
                        {selected && <span className="mr-1">✓</span>}{t.name}
                      </button>
                    );
                  })}
                  {tags.length === 0 && <span className="text-[10px] text-[#6a6660]">No tags available. Create tags first.</span>}
                </div>
              </div>
              {/* Status */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">STATUS</label>
                <select className="stc-input w-full text-[12px]" value={editProduct.status}
                  onChange={e => set("status", e.target.value)} data-testid="merch-product-status">
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              {/* Vendor Code (read-only for existing) */}
              {editProduct.vendor_code && (
                <div>
                  <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">VENDOR CODE (auto-assigned)</label>
                  <input className="stc-input w-full text-[12px] font-mono bg-black/30" value={editProduct.vendor_code} readOnly
                    data-testid="merch-product-vendor-code" />
                </div>
              )}
              {/* Available Online */}
              <div className="border-t border-white/10 pt-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editProduct.available_online} onChange={e => set("available_online", e.target.checked)} className="accent-emerald-500 w-4 h-4" data-testid="merch-product-online" />
                  <span className="font-ui text-[9px] tracking-[0.2em] text-emerald-400 uppercase">Available Online</span>
                  <span className="font-ui text-[8px] text-[#6a6660] ml-1">Enable purchasing on the listing page</span>
                </label>
              </div>
              {/* Location */}
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">LOCATION</label>
                <input className="stc-input w-full text-[12px]" value={editProduct.location ?? "The Village Antique and Vendor Market, Kingsport, TN — Booth I-12"}
                  onChange={e => set("location", e.target.value)} placeholder="Booth location..."
                  data-testid="merch-product-location" />
              </div>
            </div>
            <div className="flex gap-2 px-5 py-4 border-t border-white/10">
              <button onClick={saveProduct} disabled={saving}
                className="bg-[#C9A055] text-black px-6 py-2.5 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] disabled:opacity-50"
                data-testid="merch-product-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editProduct.id ? "Update Product" : "Create Product"}
              </button>
              <button onClick={() => setEditProduct(null)} className="btn-outline text-[10px] px-4 py-2">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Price Tag Modal ─── */}
      {printTagProduct && (
        <MerchPriceTagModal prod={printTagProduct} onClose={() => setPrintTagProduct(null)} />
      )}
    </div>
  );
}

function ProductRow({ prod, categories, onEdit, onDelete, onPrintTag }) {
  const img = prod.main_image ? resolveImage(prod.main_image) : null;
  const outOfStock = prod.quantity != null && prod.quantity <= 0;
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-3 flex gap-3 items-center" data-testid={`merch-row-${prod.id}`}>
      <div className="w-14 h-14 bg-black/50 flex-shrink-0 overflow-hidden">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={16} className="text-gray-600" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-ui text-xs text-[#F5F2EB] truncate">{prod.title}</div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {prod.vendor_code && <span className="text-[8px] tracking-wider uppercase bg-[#C9A055]/15 text-[#C9A055] px-2 py-0.5 font-bold font-mono">{prod.vendor_code}</span>}
          <span className="text-[8px] tracking-wider uppercase bg-purple-900/20 text-purple-400 px-2 py-0.5">{prod.category_name || "—"}</span>
          {prod.price != null && <span className="text-[8px] tracking-wider uppercase bg-white/5 text-gray-400 px-2 py-0.5">${Number(prod.price).toFixed(2)}</span>}
          {prod.quantity != null ? (
            <span className={`text-[8px] tracking-wider uppercase px-2 py-0.5 ${outOfStock ? "bg-red-900/30 text-red-400" : prod.quantity <= 5 ? "bg-amber-900/20 text-amber-400" : "bg-white/5 text-gray-400"}`}>
              {outOfStock ? "Out of Stock" : `Qty: ${prod.quantity}`}
            </span>
          ) : <span className="text-[8px] tracking-wider uppercase bg-white/5 text-gray-500 px-2 py-0.5">Unlimited</span>}
          {prod.status !== "active" && <span className="text-[8px] tracking-wider uppercase bg-yellow-900/30 text-yellow-400 px-2 py-0.5">Draft</span>}
          {(prod.tags || []).length > 0 && (prod.tags || []).slice(0, 3).map(t => (
            <span key={t} className="text-[8px] tracking-wider uppercase bg-purple-900/15 text-purple-300 px-1.5 py-0.5">{t}</span>
          ))}
          {(prod.tags || []).length > 3 && <span className="text-[8px] text-[#6a6660]">+{prod.tags.length - 3}</span>}
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={onPrintTag} className="btn-outline text-[10px] px-2 py-1 border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055]/10" title="Print Price Tag" data-testid={`merch-tag-${prod.id}`}>
          <Package size={12} />
        </button>
        <button onClick={onEdit} className="btn-outline text-[10px] px-2 py-1" data-testid={`merch-edit-${prod.id}`}>
          <Edit3 size={12} />
        </button>
        <button onClick={onDelete} className="btn-outline text-[10px] px-2 py-1 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`merch-del-${prod.id}`}>
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function generateMerchTagHtml(prod) {
  const vendorCode = prod.vendor_code || "MT26-XXX0000";
  const artImg = prod.main_image ? resolveImage(prod.main_image) : null;
  const price = Number(prod.price || 0);
  const hasSale = prod.on_sale && prod.sale_price != null && Number(prod.sale_price) > 0 && Number(prod.sale_price) < price;
  const displayPrice = hasSale ? Number(prod.sale_price) : price;
  const dollars = displayPrice.toFixed(0);
  const cents = (displayPrice % 1 !== 0) ? (displayPrice % 1).toFixed(2).substring(1) : ".00";
  const category = prod.category_name || "PRODUCT";
  const title = (prod.title || "UNTITLED").toUpperCase();
  const truncTitle = title.length > 28 ? title.substring(0, 26) + "..." : title;
  const slug = prod.slug || prod.id;
  const siteBase = window.location.origin;
  const productUrl = `${siteBase}/the-market/${slug}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(productUrl)}&margin=4`;

  const saleBadgeHtml = hasSale ? `<div style="position:absolute;top:0;right:0;background:#FF3B30;color:#fff;font-family:'Saira Condensed',sans-serif;font-size:10px;font-weight:800;letter-spacing:0.12em;padding:3px 8px;text-transform:uppercase;">SALE</div>` : "";

  const priceHtml = hasSale
    ? `<div style="display:flex;align-items:baseline;gap:6px;">
         <span style="font-family:'Saira Condensed',sans-serif;font-size:58px;font-weight:900;line-height:1;color:#FF3B30;">$${dollars}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#FF3B30;line-height:1;position:relative;top:-2px;">${cents}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#999;text-decoration:line-through;line-height:1;position:relative;top:-2px;margin-left:4px;">$${price.toFixed(0)}</span>
       </div>`
    : `<div style="display:flex;align-items:baseline;gap:3px;">
         <span style="font-family:'Saira Condensed',sans-serif;font-size:58px;font-weight:900;line-height:1;color:#111;">$${dollars}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#999;line-height:1;position:relative;top:-2px;">${cents}</span>
       </div>`;

  return `<div style="width:576px;height:384px;background:#FFFFFF;font-family:'DM Sans',sans-serif;position:relative;overflow:hidden;">
    <div style="position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,${hasSale ? "#FF3B30,#FF6B6B,#FF3B30" : "#C9A055,#E8C96A,#C9A055"});"></div>
    <div style="display:flex;height:340px;padding-top:4px;">
      <div style="width:210px;flex-shrink:0;display:flex;align-items:center;justify-content:center;padding:18px 8px 18px 20px;">
        <div style="width:178px;height:100%;position:relative;background:#f7f5f0;border:1px solid #e8e4dc;box-shadow:0 2px 12px rgba(0,0,0,0.08),0 0 0 1px rgba(201,160,85,0.15);">
          ${artImg
            ? `<img src="${artImg}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" crossorigin="anonymous" />`
            : `<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                <span style="font-size:8px;color:#bbb;letter-spacing:0.1em;text-transform:uppercase;">No Image</span>
              </div>`}
          <div style="position:absolute;top:0;left:0;background:#7C3AED;color:#fff;font-family:'Saira Condensed',sans-serif;font-size:9px;font-weight:800;letter-spacing:0.15em;padding:3px 8px;text-transform:uppercase;">${category}</div>
          ${saleBadgeHtml}
        </div>
      </div>
      <div style="flex:1;padding:16px 20px 16px 12px;display:flex;flex-direction:column;position:relative;">
        <div style="position:absolute;top:12px;right:16px;text-align:right;">
          <div style="font-family:'Saira Condensed',sans-serif;font-size:26px;font-weight:900;line-height:1;color:#1a1a1a;letter-spacing:0.03em;">${vendorCode}</div>
        </div>
        <div>
          <div style="font-family:'Permanent Marker',cursive;font-size:13px;color:#C9A055;line-height:1;">THE MARKER COLLECTION</div>
          <div style="font-size:8px;color:#aaa;letter-spacing:0.15em;text-transform:uppercase;margin-top:2px;">MarkerCollection.com</div>
        </div>
        <div style="height:1px;background:linear-gradient(90deg,#C9A055 0%,#e5e5e5 40%,transparent 100%);margin:10px 0 12px 0;"></div>
        <div style="font-family:'Saira Condensed',sans-serif;font-size:32px;font-weight:800;line-height:1.05;color:#111;max-width:300px;">${truncTitle}</div>
        <div style="display:flex;align-items:center;gap:7px;margin-top:8px;">
          <span style="font-size:9px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;background:#7C3AED;color:#fff;padding:2px 8px;">${category.toUpperCase()}</span>
        </div>
        <div style="flex:1;"></div>
        <div style="display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:4px;">
          ${priceHtml}
          <div style="width:72px;height:72px;border:1px solid #e5e5e5;display:flex;align-items:center;justify-content:center;background:#fff;flex-shrink:0;">
            <img src="${qrSrc}" alt="QR" style="width:64px;height:64px;object-fit:contain;" />
          </div>
        </div>
      </div>
    </div>
    <div style="position:absolute;bottom:0;left:0;right:0;height:44px;background:#111;display:flex;align-items:center;justify-content:space-between;padding:0 24px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="font-family:'Permanent Marker',cursive;font-size:12px;color:#C9A055;line-height:1;">THE MARKET</div>
        <div style="width:1px;height:14px;background:#333;"></div>
        <div style="font-size:7px;color:#777;letter-spacing:0.12em;">by The Marker Collection</div>
      </div>
      <div style="font-size:8px;color:#555;letter-spacing:0.1em;">Hats &bull; Books &bull; Prints &bull; More</div>
    </div>
  </div>`;
}

function MerchPriceTagModal({ prod, onClose }) {
  const handlePrint = () => {
    const html = generateMerchTagHtml(prod);
    const win = window.open("", "_blank", "width=700,height=500");
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Price Tag — ${prod.title}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Saira+Condensed:wght@700;800;900&family=DM+Sans:wght@400;500;700&family=Permanent+Marker&display=swap" rel="stylesheet">
      <style>
        @page { size: 6in 4in landscape; margin: 0; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { width: 6in; height: 4in; overflow: hidden; background: #fff; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head><body>${html}</body></html>`);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="merch-price-tag-modal">
      <div className="bg-[#141210] border border-white/10 max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Package size={16} className="text-[#C9A055]" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">PRICE TAG — {prod.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="btn-gold text-[10px] flex items-center gap-1.5" data-testid="merch-price-tag-print">
              <Package size={12} /> PRINT TAG
            </button>
            <button onClick={onClose} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
          </div>
        </div>
        <div className="p-6 flex justify-center overflow-auto">
          <div dangerouslySetInnerHTML={{ __html: generateMerchTagHtml(prod) }} style={{ border: "1px solid #333" }} />
        </div>
      </div>
    </div>
  );
}
